import type { Attachment, AttachmentAdapter, CompleteAttachment, PendingAttachment } from '@assistant-ui/react'
import { getDmpChatAttachmentType } from './dmpChatAttachmentType.ts'

export interface DmpChatAttachmentUploadPath {
  organisationId: number
  projectId: number
  chatHistoryId: string
}

export interface DmpChatStoredInputFile {
  inputRef?: string | null
  fileName?: string | null
  kind?: string | null
  sizeBytes?: number | null
  contentType?: string | null
}

export interface DmpChatUploadResponse {
  data?: unknown
  error?: unknown
  response?: Response
}

export type DmpChatAttachmentUploadClient = (
  path: DmpChatAttachmentUploadPath,
  body: FormData
) => Promise<DmpChatUploadResponse>

export interface DmpChatAttachmentAdapterOptions {
  organisationId: number | undefined
  projectId: number
  chatHistoryId: string
  upload: DmpChatAttachmentUploadClient
  mockMode?: boolean
  onUploadSuccess?: () => void
}

let attachmentIdSequence = 0

export function createDmpChatAttachmentAdapter(options: DmpChatAttachmentAdapterOptions): AttachmentAdapter {
  const removedAttachmentIds = new Set<string>()

  return {
    accept: '*',
    add: ({ file }) => addDmpChatAttachment(options, file, removedAttachmentIds),
    remove: async (attachment) => {
      removedAttachmentIds.add(attachment.id)
    },
    send: async (attachment) => {
      throw new Error(getAttachmentStatusMessage(attachment))
    },
  }
}

async function* addDmpChatAttachment(
  options: DmpChatAttachmentAdapterOptions,
  file: File,
  removedAttachmentIds: ReadonlySet<string>
): AsyncGenerator<PendingAttachment> {
  const pendingAttachment = createPendingAttachment(file)
  yield pendingAttachment

  try {
    const storedFile = options.mockMode ? createMockStoredInputFile(file) : await uploadDmpChatAttachment(options, file)
    options.onUploadSuccess?.()
    if (removedAttachmentIds.has(pendingAttachment.id)) {
      return
    }

    yield createCompleteAttachment(pendingAttachment, storedFile) as unknown as PendingAttachment
  } catch (error) {
    if (removedAttachmentIds.has(pendingAttachment.id)) {
      return
    }

    yield createFailedAttachment(pendingAttachment, getDmpChatUploadErrorMessage(error) ?? 'File upload failed.')
  }
}

function createPendingAttachment(file: File): PendingAttachment {
  return {
    id: createAttachmentId('pending'),
    type: getDmpChatAttachmentType(file.type, file.name),
    name: file.name,
    ...(file.type ? { contentType: file.type } : {}),
    file,
    status: { type: 'running', reason: 'uploading', progress: 0 },
    content: [],
  }
}

function createCompleteAttachment(
  pendingAttachment: PendingAttachment,
  storedFile: DmpChatStoredInputFile
): CompleteAttachment {
  const inputRef = storedFile.inputRef?.trim() ?? ''
  const filename = storedFile.fileName?.trim() ?? ''
  const kind = storedFile.kind?.trim() || 'input'
  const sizeBytes = storedFile.sizeBytes
  if (
    !inputRef ||
    !filename ||
    kind !== 'input' ||
    typeof sizeBytes !== 'number' ||
    !Number.isFinite(sizeBytes) ||
    sizeBytes < 0
  ) {
    throw new Error('The file upload returned an invalid stored input file.')
  }

  return {
    id: pendingAttachment.id,
    type: pendingAttachment.type,
    inputRef,
    name: filename,
    file: pendingAttachment.file,
    kind,
    sizeBytes,
    ...(storedFile.contentType ? { contentType: storedFile.contentType } : {}),
    status: { type: 'complete' },
    content: [],
  } as unknown as CompleteAttachment
}

function createFailedAttachment(pendingAttachment: PendingAttachment, message: string): PendingAttachment {
  return {
    ...pendingAttachment,
    status: {
      type: 'incomplete',
      reason: 'error',
      message,
    },
  } as PendingAttachment
}

async function uploadDmpChatAttachment(
  options: DmpChatAttachmentAdapterOptions,
  file: File
): Promise<DmpChatStoredInputFile> {
  if (options.organisationId == null) {
    throw new Error('The current project is not available for file uploads.')
  }

  const body = new FormData()
  body.append('file', file, file.name)
  const result = await options.upload(
    {
      organisationId: options.organisationId,
      projectId: options.projectId,
      chatHistoryId: options.chatHistoryId,
    },
    body
  )

  if (result.error || result.response?.ok === false) {
    throw await createDmpChatUploadError(result)
  }

  const files = Array.isArray(result.data) ? result.data : []
  const matchingFile = files.find(
    (candidate): candidate is DmpChatStoredInputFile =>
      isStoredInputFile(candidate) && candidate.fileName?.trim() === file.name
  )
  const storedFile = matchingFile ?? files.find(isStoredInputFile)
  if (!storedFile) {
    throw new Error('The file upload returned no stored input file.')
  }

  return {
    ...storedFile,
    contentType: storedFile.contentType ?? file.type ?? null,
    sizeBytes: storedFile.sizeBytes ?? file.size,
  }
}

function createMockStoredInputFile(file: File): DmpChatStoredInputFile {
  return {
    inputRef: `in_${createAttachmentId('mock')}`,
    fileName: file.name,
    contentType: file.type || null,
    sizeBytes: file.size,
    kind: 'input',
  }
}

function isStoredInputFile(value: unknown): value is DmpChatStoredInputFile {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

async function createDmpChatUploadError(result: DmpChatUploadResponse): Promise<Error> {
  const responseMessage = await readDmpChatUploadResponseMessage(result.response)
  const message = getDmpChatUploadErrorMessage(result.error) ?? responseMessage
  const status = result.response?.status
  return new Error(message ?? (status ? `File upload failed (${status}).` : 'File upload failed.'))
}

async function readDmpChatUploadResponseMessage(response: Response | undefined): Promise<string | null> {
  if (!response || response.ok) {
    return null
  }

  try {
    const body = await response.clone().json()
    return getDmpChatUploadErrorMessage(body)
  } catch {
    try {
      return getDmpChatUploadErrorMessage(await response.clone().text())
    } catch {
      return null
    }
  }
}

function getDmpChatUploadErrorMessage(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value.trim()
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const record = value as Record<string, unknown>
  for (const key of ['message', 'detail', 'title', 'error', 'errors']) {
    const message = getDmpChatUploadErrorMessage(record[key])
    if (message) {
      return message
    }
  }

  return null
}

function getAttachmentStatusMessage(attachment: Attachment): string {
  if (attachment.status.type === 'incomplete' && attachment.status.message) {
    return attachment.status.message
  }

  return 'The file is not ready to send.'
}

function createAttachmentId(prefix: string): string {
  const randomId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${(attachmentIdSequence += 1).toString(36)}`
  return `${prefix}-${randomId}`
}
